import React from "react";
import type { ProfileData } from "@recursive-robot/react-jsx-parser";
import { batchKey, TRIGGER_COLORS, TRIGGER_LABELS, formatMs } from "./viz";
import { useContainerWidth } from "./useContainerWidth";
import { usePanZoom } from "./usePanZoom";

const LANE_HEIGHT = 26;
const LANE_GAP = 4;
const AXIS_HEIGHT = 18;
// Every present batch stays visible and clickable, even at totalTime = 0 ms.
const MIN_BATCH_WIDTH = 12;

// Lane order top-to-bottom.
const LANES: ProfileData["trigger"][] = ["render", "callback", "react"];

export type TimelineScope = "pass" | "template";

const SCOPE_LABELS: Record<TimelineScope, string> = { pass: "Pass", template: "Template" };
const SCOPE_TITLES: Record<TimelineScope, string> = {
    pass: "Batches of the selected render cycle",
    template: "Every retained render of this template, across passes",
};

type Props = {
    /** Whatever the parent wants on the lanes — one cycle's batches, or a whole template's. */
    batches: ProfileData[];
    selectedBatchKey: string | null;
    onSelectBatch: (batch: ProfileData) => void;
    /** Pan/zoom resets when this changes (the viewed cycle / template). */
    viewKey: string;
    /** When provided, a Pass/Template scope toggle renders in the legend row. */
    scope?: TimelineScope;
    onScopeChange?: (scope: TimelineScope) => void;
};

function ProfileTimeline({ batches, selectedBatchKey, onSelectBatch, viewKey, scope, onScopeChange }: Props) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    // Fills its pane and reflows on resize (pop-out-safe — see the hook).
    const width = useContainerWidth(containerRef, 600, 240);
    // Horizontal zoom/pan: screenX = baseX * k + tx.
    const { k, tx, reset, consumeDragClick, viewportHandlers } = usePanZoom(width, containerRef);

    // Reset the view whenever the viewed subject (cycle / template / scope) changes.
    React.useEffect(() => { reset(); }, [viewKey, reset]);

    const { domainMin, domainSpan } = React.useMemo(() => {
        const { min, max } = batches.length > 0 ?
            batches.reduce((acc, elem) => {
                return {
                    min: Math.min(acc.min, elem.startTime),
                    max: Math.max(acc.max, elem.startTime + elem.totalTime)
                };
            }, { min: Infinity, max: -Infinity }) :
            { min: 0, max: 1 };
        return { domainMin: min, domainSpan: Math.max(1e-6, max - min) };
    }, [batches]);

    const baseX = (t: number) => ((t - domainMin) / domainSpan) * width;
    const screenX = (t: number) => baseX(t) * k + tx;

    const laneIndex = (trigger: ProfileData["trigger"]) => LANES.indexOf(trigger);
    const height = AXIS_HEIGHT + LANES.length * (LANE_HEIGHT + LANE_GAP);

    const selectedBatch = batches.find(b => batchKey(b) === selectedBatchKey);
    // Clicking a legend item shows that lane's batch in the flame graph; when the lane holds several
    // (callbacks), repeated clicks cycle through them (findIndex = -1 -> first batch).
    const onLegendClick = (trigger: ProfileData["trigger"]) => {
        const lane = batches.filter(b => b.trigger === trigger);
        if (!lane.length) return;
        onSelectBatch(lane[(lane.findIndex(b => batchKey(b) === selectedBatchKey) + 1) % lane.length]);
    };

    // Axis ticks (ms relative to the domain start) over the VISIBLE window, so they follow the
    // zoom/pan and re-densify at a "nice" (1/2/5 × 10^n) step instead of spreading off-screen.
    const ticks = React.useMemo(() => {
        const visibleSpan = domainSpan / k;
        const relStart = ((0 - tx) / (width * k)) * domainSpan; // ms at the viewport's left edge
        // Nice-step rounding can stretch the spacing up to 2.5×, so divide by 8 to keep at least
        // 3 (and at most 8) ticks on screen at any zoom level.
        const rawStep = visibleSpan / 8;
        const pow = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const step = [1, 2, 5, 10].map(m => m * pow).find(s => s >= rawStep) ?? rawStep;
        const first = Math.ceil(relStart / step) * step;
        return Array.from({ length: 9 }, (_, i) => domainMin + first + i * step)
            .filter(t => t - domainMin <= relStart + visibleSpan + step / 1000);
    }, [domainMin, domainSpan, k, tx, width]);

    return (
        <div className="profile-timeline">
            <div className="timeline-legend">
                {LANES.map(tr => {
                    const laneCount = batches.filter(b => b.trigger === tr).length;
                    const active = selectedBatch?.trigger === tr;
                    return (
                        <span
                            key={tr}
                            className={`legend-item${laneCount ? " clickable" : " disabled"}${active ? " active" : ""}`}
                            title={laneCount
                                ? `Show this batch in the flame graph${laneCount > 1 ? " · click again for the next one" : ""}`
                                : "No batches of this kind on the timeline"}
                            onClick={() => onLegendClick(tr)}
                        >
                            <span className="legend-swatch" style={{ background: TRIGGER_COLORS[tr] }} />
                            {TRIGGER_LABELS[tr]}
                        </span>
                    );
                })}
                <span className="timeline-hint">scroll to zoom · drag to pan</span>
                {scope && onScopeChange && (
                    <span className="scope-toggle">
                        {(Object.keys(SCOPE_LABELS) as TimelineScope[]).map(s => (
                            <button
                                key={s}
                                className={`scope-option${scope === s ? " active" : ""}`}
                                title={SCOPE_TITLES[s]}
                                onClick={() => onScopeChange(s)}
                            >
                                {SCOPE_LABELS[s]}
                            </button>
                        ))}
                    </span>
                )}
            </div>
            <div className="timeline-viewport" ref={containerRef} {...viewportHandlers}>
                <svg width={width} height={height} className="timeline-svg">
                    {/* axis ticks */}
                    {ticks.map((t, i) => {
                        const x = screenX(t);
                        if (x < -40 || x > width + 40) return null;
                        return (
                            <g key={i} transform={`translate(${x}, 0)`}>
                                <line y1={AXIS_HEIGHT} y2={height} className="timeline-gridline" />
                                <text x={2} y={12} fontSize={10} className="timeline-tick">{formatMs(t - domainMin)}</text>
                            </g>
                        );
                    })}
                    {/* batches */}
                    {batches.map(b => {
                        const x = Math.min(screenX(b.startTime), width - MIN_BATCH_WIDTH);
                        const w = Math.max(MIN_BATCH_WIDTH, (baseX(b.startTime + b.totalTime) - baseX(b.startTime)) * k);
                        const y = AXIS_HEIGHT + laneIndex(b.trigger) * (LANE_HEIGHT + LANE_GAP);
                        const selected = batchKey(b) === selectedBatchKey;
                        return (
                            <g
                                key={batchKey(b)}
                                transform={`translate(${x}, ${y})`}
                                className="timeline-batch"
                                onClick={() => { if (!consumeDragClick()) onSelectBatch(b); }}
                            >
                                <title>
                                    {`${TRIGGER_LABELS[b.trigger]} · #${b.renderId}\n`}
                                    {`Template: ${b.fileName ?? "(inline)"}\n`}
                                    {`Total: ${formatMs(b.totalTime)}  ·  Start +${formatMs(b.startTime - domainMin)}\n`}
                                    {`Nodes: ${b.nodes.length}`}
                                </title>
                                <rect
                                    width={w}
                                    height={LANE_HEIGHT}
                                    rx={2}
                                    fill={TRIGGER_COLORS[b.trigger]}
                                    opacity={selected ? 1 : 0.82}
                                    stroke={selected ? "deeppink" : "none"}
                                    strokeWidth={selected ? 1.5 : 0}
                                />
                                {w > 44 && (
                                    <text x={5} y={LANE_HEIGHT / 2} dominantBaseline="central" fontSize={10} fill="#ffffff">
                                        {formatMs(b.totalTime)}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
}

export default React.memo(ProfileTimeline);
